/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react';
import { IOrderItem } from '@/interfaces/order';
import {
  Button,
  Col,
  Form,
  Input,
  InputNumber,
  notification,
  Row,
  Select,
} from 'antd';
import PackingListItem from './packing-list-table';
import { round, sumBy } from 'lodash';
import { IPackingList, IPackingListItem } from '@/interfaces/packingList';
import { addpackingList } from '@/adapters/packingList';
import { useMutation } from '@tanstack/react-query';
interface IProps {
  orderId: number;
  orderItems: Partial<IOrderItem>[];
  onAddPackingList: () => void;
}

const PackingAddPanel = ({ orderItems, onAddPackingList, orderId }: IProps) => {
  const [form] = Form.useForm<IPackingListItem>();
  const [formPackingList] = Form.useForm<IPackingList>();
  const [packingLists, setPackingLists] = useState<Partial<IPackingListItem>[]>(
    [],
  );

  const handleUpdate = async () => {
    form
      .validateFields()
      .then(async (values: Partial<IPackingListItem>) => {
        const currOrderItem = orderItems.find(c => c.id === values.id);
        if (currOrderItem) {
          setPackingLists([
            ...packingLists,
            {
              ...currOrderItem,
              quantity: values.quantity,
              orderItem: currOrderItem,
            },
          ]);
        }
        form.resetFields();
      })
      .catch(info => {
        console.log('Validate Failed:', info);
      });
  };

  const mutations = useMutation({
    mutationFn: async (data: Partial<IPackingList>) =>
      await addpackingList(data),
    mutationKey: ['create-packing-list'],
    onSuccess: data => {
      if (data.status === 200) {
        formPackingList.resetFields();
        setPackingLists([]);
        notification.success({
          message: 'สร้าง Packing List สำเร็จ',
        });
        onAddPackingList();
      }
    },
    onError: error => {
      console.log('error', error);
    },
  });

  const handleAddPackingList = async () => {
    formPackingList
      .validateFields()
      .then(async (values: Partial<IPackingList>) => {
        const items = packingLists.map(p => {
          const item: Partial<IPackingListItem> = {
            id: 0,
            lotNumber: p.lotNumber || '',
            netweight: round((p.quantity || 0) * (p.conversion || 0), 2),
            package: p.quantity || 0,
            productCode: p.productCode,
            productName: p.productName,
            quantity: round((p.quantity || 0) * (p.conversion || 0), 2),
          };
          return item;
        });
        const model: Partial<IPackingList> = {
          dimension: values.dimension || '',
          grossWeight: values.grossWeight || 0,
          id: 0,
          orderId: orderId,
          netWeight: sumBy(packingLists || [], item =>
            round((item.quantity || 0) * (item.conversion || 0), 2),
          ),
          packingListItems: items,
        };
        await mutations.mutateAsync(model);
        // onAddPackingList(model);
      })
      .catch(info => {
        console.log('Validate Failed:', info);
      });
  };

  const itemOptions = orderItems.map(o => {
    return {
      value: o.id,
      label: `${o.productCode} - ${o.productName}- ${o.lotNumber}`,
    };
  });

  const onDeleRecord = (rec: Partial<IOrderItem>) => {
    setPackingLists(prev => prev.filter(p => p.id !== rec.id));
  };

  const totalQuantity = sumBy(packingLists || [], item =>
    round((item.quantity || 0) * (item.conversion || 0), 2),
  );

  return (
    <div className="flex flex-col gap-3 border-2 p-4 rounded-lg border-red-500 mt-10">
      <PackingListItem
        orderItems={packingLists || []}
        isPacking
        onDelete={onDeleRecord}
      />
      <div className="flex w-full flex-row gap-4 justify-between">
        <div className="flex w-full border p-2 rounded-lg border-red-500">
          <Form
            form={form}
            name="add-item"
            onFinish={handleUpdate}
            layout="vertical"
            className="w-full"
          >
            <Row gutter={12} className="flex items-end">
              <Col span={24} md={12}>
                <Form.Item<IPackingListItem>
                  label="รายการ"
                  name="id"
                  rules={[{ required: true, message: 'เลือกรายการ' }]}
                >
                  <Select
                    className="w-full"
                    size="large"
                    placeholder="เลือกรายการ"
                    options={itemOptions || []}
                    showSearch
                    filterOption={(input, option) =>
                      (option?.label ?? '')
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  />
                </Form.Item>
              </Col>
              <Col span={24} md={6}>
                <Form.Item<IPackingListItem>
                  label="จำนวน Package"
                  name="quantity"
                  rules={[{ required: true, message: 'จำนวน Package' }]}
                >
                  <InputNumber
                    size="large"
                    className="w-full"
                    placeholder="จำนวน Package"
                  />
                </Form.Item>
              </Col>
              <Form.Item>
                <Button
                  ghost
                  key="submit"
                  htmlType="submit"
                  type="primary"
                  form="add-item"
                  size="large"
                >
                  เพิ่มรายการ
                </Button>
              </Form.Item>
            </Row>
          </Form>
        </div>
        <div className="flex w-full items-end border p-2 rounded-lg border-red-500">
          <Form
            form={formPackingList}
            name="add-packing-list"
            onFinish={handleAddPackingList}
            layout="vertical"
            className="w-full"
          >
            <Row gutter={12} className="flex justify-end items-end">
              <Col span={24} md={6}>
                <Form.Item<IPackingList>
                  label="Dimension"
                  name="dimension"
                  rules={[{ required: true, message: 'Dimension' }]}
                >
                  <Input
                    size="large"
                    className="w-full"
                    placeholder="ex. 100x120x80"
                  />
                </Form.Item>
              </Col>
              <Col span={24} md={4}>
                <Form.Item<IPackingList> label="NetWeight">
                  <InputNumber
                    size="large"
                    className="w-full"
                    placeholder="NetWeight"
                    value={totalQuantity}
                  />
                </Form.Item>
              </Col>
              <Col span={24} md={4}>
                <Form.Item<IPackingList>
                  label="GrossWeight"
                  name="grossWeight"
                  rules={[{ required: true, message: 'GrossWeight' }]}
                >
                  <InputNumber
                    size="large"
                    className="w-full"
                    placeholder="GrossWeight"
                  />
                </Form.Item>
              </Col>
              <Form.Item>
                <Button
                  key="submit"
                  htmlType="submit"
                  type="primary"
                  form="add-packing-list"
                  size="large"
                  className="mr-2"
                  disabled={!packingLists.length}
                >
                  บันทึกรายการ
                </Button>
              </Form.Item>
            </Row>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default PackingAddPanel;
